import { useState } from 'react';
import { UseFormReturn, SubmitHandler } from 'react-hook-form';

import { StarPicker } from 'components/StarPicker';
import { Form } from 'components/form';
import { Colors } from 'constants/ui';
import { useReducedMotion } from 'utils/useReducedMotion';

import { PhotoCapture } from './photo-capture';
import { AudioRecorder } from './audio-recorder';
import { VideoRecorder } from './video-recorder';

import * as styled from '../styles/form';

const FALLBACK_ACCENT = Colors.accent;

const TIMEFRAME_OPTIONS = [
  { value: 'less_than_30_min', label: 'Less than 30 mins' },
  { value: 'thirty_min_to_two_hours', label: '30 mins – 2 hours' },
  { value: 'same_day', label: 'Same day' },
  { value: 'a_few_days', label: 'A few days' },
  { value: 'one_to_two_weeks', label: '1–2 weeks' },
  { value: 'one_plus_month', label: '1+ month' },
];

type Props = {
  form: UseFormReturn<Review.ReviewFormValues>;
  onSubmit: SubmitHandler<Review.ReviewFormValues>;
  formData: Kudable.ReviewFormData;
  overallRating: number;
  categoryRatings: Record<string, number>;
  isUploading: boolean;
  uploadError: string | null;
  uploadedAssets: Review.UploadedReviewAsset[];
  maxMedia: number;
  onCategoryChange: (category: string, rating: number) => void;
  onAddMedia: (file: File) => void;
  onRemoveMedia: (id: string) => void;
  participantRatings: Record<string, number>;
  participantComments: Record<string, string>;
  onParticipantRatingChange: (memberId: string, rating: number) => void;
  onParticipantCommentChange: (memberId: string, value: string) => void;
};

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('') || '?';

const StarsDisplay = ({ value }: { value: number }) => (
  <span aria-hidden>
    {[1, 2, 3, 4, 5].map((starValue) => (
      <span key={starValue}>{starValue <= value ? '★' : '☆'}</span>
    ))}
  </span>
);

export const ReviewForm = ({
  form,
  onSubmit,
  formData,
  overallRating,
  categoryRatings,
  isUploading,
  uploadError,
  uploadedAssets,
  maxMedia,
  onCategoryChange,
  onAddMedia,
  onRemoveMedia,
  participantRatings,
  participantComments,
  onParticipantRatingChange,
  onParticipantCommentChange,
}: Props) => {
  const { provider, link, categories, media } = formData;
  const isGroup = link.is_group;
  const reducedMotion = useReducedMotion();
  const accent = (provider as any).accent_color || FALLBACK_ACCENT;

  const reviewerName = form.watch('reviewer_name') || link.client_name || '';
  const result = form.watch('comment') || '';
  const improvementSuggestion = form.watch('improvement_suggestion');
  const improvementPublic = form.watch('improvement_public');
  const timeframe = form.watch('timeframe') ?? '';

  const meta = [provider.title, link.location].filter(Boolean).join(' · ');

  const mediaEnabled = media?.enabled === true;
  const videosEnabled = media?.videos_enabled === true;
  const audioEnabled = media?.audio_enabled === true;
  const atLimit = uploadedAssets.length >= maxMedia;
  const [activeCapture, setActiveCapture] = useState<
    'photo' | 'audio' | 'video' | null
  >(null);
  const hasMediaRecorder = typeof MediaRecorder !== 'undefined';

  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(reviewerName);

  const ratingsTouched = isGroup
    ? Object.values(participantRatings).some((rating) => rating > 0)
    : overallRating > 0;
  const showWritten = ratingsTouched;
  const showExtras = showWritten && result.trim().length > 0;

  const canSubmit = ratingsTouched && reviewerName.trim().length > 0;

  return (
    <Form form={form} onSubmit={onSubmit}>
      {!reviewerName || editingName ? (
        <styled.IdentityInputRow>
          <styled.IdentityInput
            autoFocus
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
            placeholder="Your name"
            aria-label="Your name"
          />
          <styled.IdentitySaveBtn
            $accent={accent}
            type="button"
            disabled={!draftName.trim()}
            onClick={() => {
              const next = draftName.trim();
              if (!next) return;
              form.setValue('reviewer_name', next);
              setEditingName(false);
            }}
          >
            Save
          </styled.IdentitySaveBtn>
        </styled.IdentityInputRow>
      ) : (
        <styled.IdentityBar>
          <styled.IdentityAvatar $accent={accent}>
            {initialsOf(reviewerName)}
          </styled.IdentityAvatar>
          <span>
            Reviewing as <strong>{reviewerName}</strong>
          </span>
          <styled.IdentityEdit
            $accent={accent}
            type="button"
            onClick={() => {
              setDraftName(reviewerName);
              setEditingName(true);
            }}
          >
            Edit
          </styled.IdentityEdit>
        </styled.IdentityBar>
      )}

      <styled.ProviderName>
        {provider.first_name} {provider.last_name}
      </styled.ProviderName>
      {meta ? <styled.ProviderMeta>{meta}</styled.ProviderMeta> : null}

      {/* SECTION 1 — Ratings */}
      {isGroup ? (
        <styled.Section $reducedMotion={reducedMotion}>
          <styled.SectionHint>Rate each team member.</styled.SectionHint>
          {link.participants.map((participant) => (
            <styled.GroupRow key={participant.member_id}>
              <styled.GroupName>
                {participant.name}
                {participant.role && (
                  <styled.GroupRole> · {participant.role}</styled.GroupRole>
                )}
              </styled.GroupName>
              <StarPicker
                value={participantRatings[participant.member_id] ?? 0}
                onChange={(rating) =>
                  onParticipantRatingChange(participant.member_id, rating)
                }
                label={`Rate ${participant.name}`}
              />
              <styled.GroupTextarea
                value={participantComments[participant.member_id] ?? ''}
                onChange={(event) =>
                  onParticipantCommentChange(
                    participant.member_id,
                    event.target.value,
                  )
                }
                placeholder={`Comment about ${participant.name} (optional)`}
              />
            </styled.GroupRow>
          ))}
        </styled.Section>
      ) : (
        <styled.Section $reducedMotion={reducedMotion}>
          <styled.SectionHint>
            Tap to rate. Overall comes from these.
          </styled.SectionHint>
          <styled.CategoryList>
            {categories.map((category) => (
              <styled.CategoryRow key={category}>
                <styled.CategoryLabel>
                  {category.replace(/_/g, ' ')}
                </styled.CategoryLabel>
                <StarPicker
                  value={categoryRatings[category] ?? 0}
                  onChange={(rating) => onCategoryChange(category, rating)}
                  label={`Rate ${category.replace(/_/g, ' ')}`}
                />
              </styled.CategoryRow>
            ))}
          </styled.CategoryList>

          <styled.OverallSummary
            $accent={accent}
            $hasRating={overallRating > 0}
          >
            <styled.OverallLabel>Overall</styled.OverallLabel>
            <styled.OverallStars $accent={accent}>
              <StarsDisplay value={overallRating} />
              {overallRating > 0 ? (
                <styled.OverallNumber>{overallRating}.0</styled.OverallNumber>
              ) : null}
            </styled.OverallStars>
          </styled.OverallSummary>
        </styled.Section>
      )}

      {/* SECTION 2 — Written feedback */}
      {showWritten ? (
        <styled.Section $reducedMotion={reducedMotion} aria-live="polite">
          <styled.FieldGroup>
            <styled.FieldLabel htmlFor="review-result">
              Tell us about your experience working with them.
            </styled.FieldLabel>
            <styled.Textarea
              id="review-result"
              {...form.register('comment')}
              placeholder={
                isGroup
                  ? 'What was it like working with the team?'
                  : `What was it like working with ${provider.first_name}?`
              }
            />
          </styled.FieldGroup>

          <styled.FieldGroup>
            <styled.FieldLabel htmlFor="review-improvement">
              What could they improve? (optional)
            </styled.FieldLabel>
            <styled.Textarea
              id="review-improvement"
              {...form.register('improvement_suggestion')}
              placeholder="Anything that would have made it better?"
            />
            {improvementSuggestion.trim().length > 0 ? (
              <styled.ImprovementVisibility>
                <styled.SoftToggle>
                  <input
                    type="radio"
                    name="improvement-visibility"
                    checked={!improvementPublic}
                    onChange={() => form.setValue('improvement_public', false)}
                  />
                  <span>Only the provider sees this (private)</span>
                </styled.SoftToggle>
                <styled.SoftToggle>
                  <input
                    type="radio"
                    name="improvement-visibility"
                    checked={improvementPublic}
                    onChange={() => form.setValue('improvement_public', true)}
                  />
                  <span>Show on their profile to help others</span>
                </styled.SoftToggle>
              </styled.ImprovementVisibility>
            ) : null}
          </styled.FieldGroup>
        </styled.Section>
      ) : null}

      {/* SECTION 3 — Optional extras */}
      {showExtras ? (
        <styled.Section $reducedMotion={reducedMotion} aria-live="polite">
          <styled.FieldGroup>
            <styled.FieldLabel htmlFor="review-timeframe">
              How long did it take?
            </styled.FieldLabel>
            <styled.Select
              id="review-timeframe"
              value={timeframe}
              onChange={(event) =>
                form.setValue('timeframe', event.target.value)
              }
            >
              <option value="">—</option>
              {TIMEFRAME_OPTIONS.map((timeframeOption) => (
                <option key={timeframeOption.value} value={timeframeOption.value}>
                  {timeframeOption.label}
                </option>
              ))}
            </styled.Select>
          </styled.FieldGroup>

          {mediaEnabled ? (
            <styled.FieldGroup>
              <styled.FieldLabel>
                Add a photo, voice note, or video (optional)
              </styled.FieldLabel>

              {uploadedAssets.length > 0 ? (
                <styled.ThumbsRow>
                  {uploadedAssets.map((asset) => {
                    if (asset.kind === 'audio') {
                      return (
                        <styled.AudioThumb key={asset.id}>
                          <styled.AudioIcon>🎤</styled.AudioIcon>
                          <styled.AudioPlayer controls src={asset.url} />
                          <styled.RemoveBtn
                            type="button"
                            aria-label="Remove"
                            onClick={() => onRemoveMedia(asset.id)}
                          >
                            ✕
                          </styled.RemoveBtn>
                        </styled.AudioThumb>
                      );
                    }
                    return (
                      <styled.Thumb key={asset.id}>
                        {asset.kind === 'video' ? (
                          <>
                            <video src={asset.url} muted />
                            <styled.PlayBadge>▶</styled.PlayBadge>
                          </>
                        ) : (
                          <img src={asset.url} alt="" />
                        )}
                        <styled.RemoveBtn
                          type="button"
                          aria-label="Remove"
                          onClick={() => onRemoveMedia(asset.id)}
                        >
                          ✕
                        </styled.RemoveBtn>
                      </styled.Thumb>
                    );
                  })}
                </styled.ThumbsRow>
              ) : null}

              {activeCapture === 'photo' ? (
                <PhotoCapture
                  onCaptureComplete={(file) => {
                    setActiveCapture(null);
                    onAddMedia(file);
                  }}
                  onCancel={() => setActiveCapture(null)}
                />
              ) : null}
              {activeCapture === 'audio' ? (
                <AudioRecorder
                  onRecordingComplete={(file) => {
                    setActiveCapture(null);
                    onAddMedia(file);
                  }}
                  onCancel={() => setActiveCapture(null)}
                />
              ) : null}
              {activeCapture === 'video' ? (
                <VideoRecorder
                  onRecordingComplete={(file) => {
                    setActiveCapture(null);
                    onAddMedia(file);
                  }}
                  onCancel={() => setActiveCapture(null)}
                />
              ) : null}

              {!atLimit && !activeCapture ? (
                <styled.CaptureRow>
                  <styled.CaptureBtn
                    type="button"
                    onClick={() => setActiveCapture('photo')}
                    disabled={isUploading}
                  >
                    📷 Photo
                  </styled.CaptureBtn>
                  {audioEnabled && hasMediaRecorder ? (
                    <styled.CaptureBtn
                      type="button"
                      onClick={() => setActiveCapture('audio')}
                      disabled={isUploading}
                    >
                      🎤 Voice note
                    </styled.CaptureBtn>
                  ) : null}
                  {videosEnabled && hasMediaRecorder ? (
                    <styled.CaptureBtn
                      type="button"
                      onClick={() => setActiveCapture('video')}
                      disabled={isUploading}
                    >
                      🎥 Video
                    </styled.CaptureBtn>
                  ) : null}
                  {isUploading ? (
                    <styled.UploadingHint>Uploading…</styled.UploadingHint>
                  ) : null}
                </styled.CaptureRow>
              ) : null}

              {uploadError ? (
                <styled.UploadError>{uploadError}</styled.UploadError>
              ) : null}
            </styled.FieldGroup>
          ) : null}

          <styled.SoftToggle>
            <input type="checkbox" {...form.register('contactable')} />
            <span>
              I'm open to answering questions about this experience. Your
              contact details are never shared — replies route through Kudable.
            </span>
          </styled.SoftToggle>
        </styled.Section>
      ) : null}

      <styled.SubmitButton
        type="submit"
        $accent={accent}
        disabled={!canSubmit || form.formState.isSubmitting}
      >
        {form.formState.isSubmitting ? 'Submitting…' : 'Submit Review'}
      </styled.SubmitButton>
      {!ratingsTouched ? (
        <styled.SubmitHint>Tap a star above to start.</styled.SubmitHint>
      ) : null}
    </Form>
  );
};
